import { NgZone } from '@angular/core';
import { CategoryStoreActions, DeviceStoreActions, LocationsRouterStoreSelectors } from '@era/stores';
import { Store } from '@ngrx/store';
import { DeviceDto as Device, Location, Room } from '@rocksoft/locations-sdk';
import { CategoryType, RouteGroupRule } from '@rocksoft/shared';
import { lastValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

interface RoomStatusEvent {
  locationId: string;
  roomId: string;
  status: 'CheckedIn' | 'CheckedOut';
}

/**
 * Creates route group rules for the Locations hub
 */
export function createLocationsRules(store: Store, zone: NgZone): RouteGroupRule[] {
  return [
    {
      name: 'locations-group',
      groupName: () => 'locations-group',
      urlPattern: /^\/places(?:\/?$|\/brands\/[^/]+\/locations\/[^/]+\/?$)/,
      hub: 'locations',
      eventHandlers: [
        {
          eventName: 'OnLocationChanged',
          handler: (location: Location) => {
            zone.run(() => {
              store.dispatch(CategoryStoreActions.LocationsSignalRHubActions.locationUpdated({ location }));
            });
          },
        },
        {
          eventName: 'OnRoomChanged',
          handler: (room: Room) => {
            zone.run(() => {
              store.dispatch(CategoryStoreActions.LocationsSignalRHubActions.roomUpdated({ room }));
            });
          },
        },
        {
          eventName: 'OnCategoryDeleted',
          handler: (categoryType: CategoryType, categoryId: string) => {
            zone.run(() => {
              store.dispatch(
                CategoryStoreActions.LocationsSignalRHubActions.categoryDeleted({ categoryType, categoryId })
              );
            });
          },
        },
      ],
    },
    // Devices route - handles device changes and sync status
    {
      name: 'devices-location-group',
      urlPattern: /^\/devices\/brands\/[^/]+\/locations\/[^/]+/, // (/devices/brands/:brandId/locations/:locationId)
      hub: 'locations',
      groupName: async (store) => {
        const location = await lastValueFrom(store.select(LocationsRouterStoreSelectors.selectLocation).pipe(take(1)));
        if (!location?.categoryId) return null;
        return `location_${location.categoryId}_devices`;
      },
      eventHandlers: [
        {
          eventName: 'OnDeviceChanged',
          handler: (device: Device) => {
            zone.run(() => {
              store.dispatch(
                DeviceStoreActions.DeviceSignalRHubActions.deviceChanged({
                  device: {
                    ...device,
                    lastSeen: device.lastSeen ?? undefined,
                    id: device.id ?? '',
                    deviceId: device.deviceId,
                  },
                })
              );
            });
          },
        },
        {
          eventName: 'OnDeviceRemoved',
          handler: (device: Device) => {
            zone.run(() => {
              store.dispatch(
                DeviceStoreActions.DeviceSignalRHubActions.deviceRemoved({
                  device: {
                    ...device,
                    lastSeen: device.lastSeen ?? undefined,
                    id: device.id ?? '',
                    deviceId: device.deviceId,
                  },
                })
              );
            });
          },
        },
      ],
    },

    // Location route - handles room status updates
    {
      name: 'location-group',
      urlPattern: /^\/places\/brands\/[^/]+\/locations\/[^/]+/, // (/places/brands/:brandId/locations/:locationId)
      hub: 'locations',
      groupName: async (store) => {
        const location = await lastValueFrom(store.select(LocationsRouterStoreSelectors.selectLocation).pipe(take(1)));
        if (!location?.categoryId) return null;
        return `location_${location.categoryId}`;
      },
      eventHandlers: [
        {
          eventName: 'OnRoomStatusEvent',
          handler: (roomStatus: RoomStatusEvent) => {
            zone.run(() => {
              store.dispatch(
                CategoryStoreActions.CategorySignalRHubActions.categoryUpdated({
                  categoryType: 'Room',
                  categoryId: roomStatus.roomId,
                  status: roomStatus.status,
                })
              );
            });
          },
        },
      ],
    },
  ];
}
